import { useEffect, useState } from "react";
import { StickyNote, Trash2, Pencil, ImageOff, Film, ExternalLink } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl, apiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useConfirm } from "../components/ConfirmDialog";

const relativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function Notes() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [notes, setNotes] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const load = () => api.get("/notes").then((r) => setNotes(r.data)).catch(() => setNotes([]));
  useEffect(() => { load(); }, []);

  const startEdit = (n) => { setEditingId(n.id); setEditingText(n.text); };
  const cancelEdit = () => { setEditingId(null); setEditingText(""); };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!text) return cancelEdit();
    try {
      const { data } = await api.put(`/notes/${editingId}`, { text });
      setNotes((prev) => prev.map((n) => (n.id === editingId ? { ...n, ...data } : n)));
      cancelEdit();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeNote = async (id) => {
    if (!(await confirm({
      title: "Delete this note?",
      message: "This can't be undone.",
      tone: "danger",
      confirmText: "Delete note",
    }))) return;
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openAd = (n) => {
    if (n.ad_available) navigate(`/?ad=${n.ad_id}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="fade-up text-center">
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#000000] tracking-tight uppercase">My Notes</h1>
          <p className="text-gray-500 mt-2">Every idea, hook, and detail you&rsquo;ve captured. Kept even if you unfavorite an ad.</p>
        </div>
        {notes === null ? (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#FC861E] spin-slow" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-24" data-testid="notes-empty-state">
            <StickyNote size={40} className="mx-auto text-gray-300" />
            <p className="font-display font-bold text-xl text-[#000000] mt-4">No notes yet</p>
            <p className="text-gray-500 mt-2 text-sm">Open any ad and jot down what inspires you.</p>
            <Link to="/" className="inline-block mt-6 bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors">
              Browse the Ad Wall
            </Link>
          </div>
        ) : (
          <div className="mt-10 space-y-3" data-testid="notes-list">
            {notes.map((n) => (
              <div key={n.id} className="flex gap-4 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#FC861E] transition-colors" data-testid={`note-card-${n.id}`}>
                {/* Ad thumbnail or fallback */}
                <button
                  onClick={() => openAd(n)}
                  disabled={!n.ad_available}
                  className={`w-20 h-20 rounded-lg overflow-hidden shrink-0 relative ${n.ad_available ? "cursor-pointer hover:opacity-80 transition-opacity" : "cursor-not-allowed"}`}
                  title={n.ad_available ? "Open the ad" : "This ad is no longer in the library"}
                >
                  {n.ad_available && n.ad_thumbnail ? (
                    <img src={mediaUrl(n.ad_id, "thumb")} alt={n.ad_title} className="w-full h-full object-cover" />
                  ) : n.ad_available ? (
                    <div className="w-full h-full bg-[#4A0A66] flex items-center justify-center">
                      <Film size={22} className="text-[#4A0A66]" />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center" data-testid={`note-ad-deleted-${n.id}`}>
                      <ImageOff size={22} className="text-gray-400" />
                    </div>
                  )}
                </button>
                {/* Note body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => openAd(n)}
                      disabled={!n.ad_available}
                      className={`font-display font-bold text-sm text-[#000000] truncate text-left ${n.ad_available ? "hover:text-[#DF1283] cursor-pointer" : "cursor-not-allowed"}`}
                    >
                      {n.ad_title || "Untitled"}
                    </button>
                    {n.ad_available && <ExternalLink size={11} className="text-gray-400 shrink-0" />}
                    {!n.ad_available && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">Ad removed</span>
                    )}
                  </div>
                  {editingId === n.id ? (
                    <div className="mt-2">
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                        autoFocus
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button onClick={saveEdit} className="text-[10px] font-bold uppercase text-white bg-[#000000] rounded-full px-3 py-1 hover:bg-[#4A0A66]" data-testid={`note-save-btn-${n.id}`}>Save</button>
                        <button onClick={cancelEdit} className="text-[10px] font-bold uppercase text-gray-500 hover:text-[#000000] px-2 py-1">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1 leading-relaxed">{n.text}</p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-gray-400 uppercase tracking-[0.06em]">{relativeTime(n.updated_at || n.created_at)}</span>
                    {editingId !== n.id && (
                      <div className="flex gap-1">
                        <button onClick={() => startEdit(n)} className="p-1.5 rounded-full text-gray-400 hover:text-[#000000] hover:bg-gray-100 transition-colors" title="Edit note" data-testid={`note-edit-btn-${n.id}`}><Pencil size={12} /></button>
                        <button onClick={() => removeNote(n.id)} className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-[#DF1283] transition-colors" title="Delete note" data-testid={`note-delete-btn-${n.id}`}><Trash2 size={12} /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
